const { Core, onPlyrLoad, Utils } = Global;

customElements.define('background-video', class extends Core {
    elements = {
        $: ['template', 'content']
    }

    propTypes = {
        'desktop-only': Boolean
    }

    inited = false;
    videoLoaded = false;

    render() {
        if (this.prop('desktop-only') && document.documentElement.clientWidth <= 992) {
            this.remove();
            return;
        }

        this.autoplay = this.dataset.autoplay === 'true';
        this.controls = this.dataset.controls === 'true';
        this.muted = this.dataset.muted === 'true';

        this._forwardToPrevTime = this._forwardToPrevTime.bind(this);
        this._handlePlaying = this._handlePlaying.bind(this);
        this._handleClick = this._handleClick.bind(this);

        if (this.autoplay) {
            onPlyrLoad(this._observe.bind(this));
        } else {
            this.addEventListener('click', this._handleClick);
            this.style.cursor = 'pointer';
        }
    }

    _handleClick() {
        if (this.videoLoaded) return;

        this.videoLoaded = true;
        this.removeEventListener('click', this._handleClick);
        this.style.cursor = 'default';
        onPlyrLoad(this._loadAndPlayVideo.bind(this));
    }

    _loadAndPlayVideo() {
        this._initContainer();

        if (this.playerContainer.tagName === 'VIDEO') {
            this._initHostedVideo(true);
            return;
        }

        this._initEmbeddedVideo(true);
    }

    _observe() {
        this.intersectionObserver.observe(this);
    }

    _initPlayer([entry]) {
        if (!entry.isIntersecting) {
            this.player?.pause();
            return;
        }

        if (this.player) {
            if (this.autoplay) this.player.play();
            return;
        }

        this._initContainer();

        if (this.playerContainer.tagName === 'VIDEO') {
            this._initHostedVideo(this.autoplay);
            return;
        }

        this._initEmbeddedVideo(this.autoplay);
    }

    _initHostedVideo(shouldPlay) {
        this.player = this.playerContainer;
        this.player.muted = this.muted;
        this.player.controls = this.controls;
        this.player.loop = true;

        if (shouldPlay) this.player.play().catch(() => {});

        this.player.addEventListener('playing', this._handlePlaying);
        this.player.addEventListener('error', () => this.remove());
    }

    _initEmbeddedVideo(shouldPlay) {
        const controls = this.controls
            ? ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen']
            : [];

        this.player = new window.Plyr(this.playerContainer, {
            muted: this.muted,
            controls,
            clickToPlay: this.controls,
            fullscreen: { enabled: this.controls },
            settings: this.controls ? ['quality', 'speed'] : [],
            autoplay: shouldPlay,
            loop: { active: true }
        });

        if (shouldPlay) {
            this.player.once('ready', () => this.player.play().catch(() => {}));
        }

        this.prevTime && this.player.on('play', this._forwardToPrevTime);
        this.player.on('ended', () => this.player.restart());
        this.player.once('error', () => this.remove());
        this.player.on('playing', this._handlePlaying);
    }

    _handlePlaying() {
        if (this.inited) return;

        this.inited = true;
        Utils.$state(this, 'inited');
    }

    _initContainer() {
        this.playerContainer = this.$('template').content.cloneNode(true).firstElementChild;
        this.$('content').appendChild(this.playerContainer);
    }

    _forwardToPrevTime() {
        if (this.player.forward) this.player.forward(this.prevTime);
    }

    destroy() {
        this.removeEventListener('click', this._handleClick);

        if (this.player) {
            if (this.player.tagName === 'VIDEO') {
                this.prevTime = this.player.currentTime;
                this.player.removeEventListener('playing', this._handlePlaying);
                this.player.pause();
            } else {
                this.prevTime = this.player.currentTime;
                this.player.off('play', this._forwardToPrevTime);
                this.player.off('playing', this._handlePlaying);
                this.player.destroy();
            }

            this.playerContainer?.remove();
            this.player = null;
        }

        this.inited = false;
        this.videoLoaded = false;
        Utils.$state(this, 'inited', false);
        this.intersectionObserver?.disconnect();
    }

    get intersectionObserver() {
        return new IntersectionObserver(this._initPlayer.bind(this), {
            rootMargin: '0px -10% 0px 0px'
        });
    }
});
